import { Box, HBox, VBox } from "@ui/box/Box";
import { Select, type ISelectItemProps } from "@ui/select/Select";
import { Typo } from "@ui/text/Typo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { Spinner } from "@ui/spinner/Spinner";
import { S_UNIVERSAL_DIALOG } from "src/view/components/universalDialog/signals";
import { useIssuer } from "./IssuerProvider";
import { getIssuerIdentity, getIssuerDisplayName } from "./issuerIdentity";
import { MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport";

type IssuerSelectItemProps = ISelectItemProps<ICompanyIssuer> & {
	dark?: boolean;
	compact?: boolean;
	interactive?: boolean;
};

const IssuerSelectItem = ({ value, prompt = false, selected = false, dark = false, compact = false, interactive = true }: IssuerSelectItemProps) => {
	if (!value) return null;

	const identity = getIssuerIdentity(value.issuer);
	const name = getIssuerDisplayName(value);

	if (prompt) {
		return (
			<HBox
				sx={{
					width: "100%",
					height: "100%",
					minWidth: 0,
					alignItems: "center",
					gap: "8px",
					padding: compact ? "0 9px 0 10px" : "0 10px 0 11px",
					pointerEvents: "none",
				}}
			>
				<Box
					aria-hidden="true"
					sx={{
						flex: "0 0 7px",
						width: "7px",
						height: "7px",
						borderRadius: "50%",
						backgroundColor: identity.accent,
					}}
				/>
				<Typo
					sx={{
						flex: "1 1 auto",
						minWidth: 0,
						color: dark ? "#FFFFFF" : identity.ink,
						fontSize: compact ? "0.76rem" : "0.8rem",
						fontWeight: 800,
						lineHeight: 1,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{name}
				</Typo>
				{interactive && (
					<Box
						data-issuer-chevron="true"
						aria-hidden="true"
						sx={{
							display: "flex",
							flex: "0 0 16px",
							width: "16px",
							height: "16px",
							alignItems: "center",
							justifyContent: "center",
							color: dark ? "rgba(255,255,255,.7)" : "#64748B",
							transition: "transform .2s ease",
						}}
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path d="M4.5 6.25 8 9.75l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</Box>
				)}
			</HBox>
		);
	}

	return (
		<HBox
			data-select-option-content="true"
			sx={{
				width: "100%",
				minHeight: "56px",
				alignItems: "center",
				gap: "10px",
				padding: "8px 10px",
				borderRadius: "10px",
				backgroundColor: selected ? identity.surface : "transparent",
				boxShadow: selected ? `inset 0 0 0 1px ${identity.accent}33` : "none",
				transition: "background-color .16s ease, box-shadow .16s ease, transform .16s ease",
				"&:hover": selected
					? {}
					: {
							backgroundColor: identity.surface,
							transform: "translateX(1px)",
						},
				[MOBILE]: {
					minHeight: "64px",
					padding: "10px 12px",
				},
			}}
		>
			<Box
				aria-hidden="true"
				sx={{
					display: "flex",
					flex: "0 0 26px",
					width: "26px",
					height: "26px",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: "9px",
					backgroundColor: identity.surface,
				}}
			>
				<Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: identity.accent }} />
			</Box>
			<VBox gap="3px" sx={{ flex: "1 1 auto", minWidth: 0 }}>
				<HBox gap="6px" sx={{ minWidth: 0, alignItems: "center" }}>
					<Typo
						sx={{
							minWidth: 0,
							color: dark ? "#FFFFFF" : identity.ink,
							fontSize: "0.82rem",
							fontWeight: selected ? 800 : 700,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{name}
					</Typo>
					{value.is_default && (
						<Typo
							sx={{
								flexShrink: 0,
								padding: "2px 5px",
								borderRadius: "999px",
								backgroundColor: dark ? "rgba(255,255,255,.09)" : "rgba(255,255,255,.78)",
								color: dark ? "rgba(255,255,255,.68)" : "#64748B",
								fontSize: "0.52rem",
								fontWeight: 800,
								letterSpacing: "0.04em",
								textTransform: "uppercase",
							}}
						>
							Default
						</Typo>
					)}
				</HBox>
				<Typo
					sx={{
						color: dark ? "rgba(255,255,255,.62)" : "#64748B",
						fontSize: "0.68rem",
						fontWeight: 600,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCurrencyWithNumberToSymbolWithNumber(value.balance, value.currency)} available
				</Typo>
			</VBox>
			<Box
				aria-hidden="true"
				sx={{
					display: "flex",
					flex: "0 0 22px",
					width: "22px",
					height: "22px",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: "50%",
					backgroundColor: selected ? identity.accent : "transparent",
					color: "#FFFFFF",
					opacity: selected ? 1 : 0,
				}}
			>
				<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
					<path d="m4 8.25 2.5 2.5L12 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</Box>
		</HBox>
	);
};

export const IssuerSwitcher = ({ compact = false, dark = false }: { compact?: boolean; dark?: boolean }) => {
	const { issuers, selectedIssuer, selectedIssuerId, loading, balanceSyncing, error, selectIssuer, refreshIssuers } = useIssuer();
	const balanceLoading = loading || balanceSyncing;
	const balanceUnavailable = Boolean(error) && !balanceLoading;
	const foreground = dark ? "#FFFFFF" : "#0F172A";
	const muted = dark ? "rgba(255,255,255,.58)" : "#64748B";
	const border = dark ? "rgba(255,255,255,.16)" : "#DCE4EA";
	const background = dark ? "rgba(255,255,255,.07)" : "#F8FAFC";
	const identity = getIssuerIdentity(selectedIssuerId);
	const selectedIssuerLabel = selectedIssuer ? getIssuerDisplayName(selectedIssuer) : (selectedIssuerId === null ? "No issuer" : "Card issuer");

	if (error && issuers.length === 0) {
		return (
			<Box
				element="button"
				type="button"
				title={error}
				onClick={() => void refreshIssuers()}
				sx={{ border: `1px solid ${border}`, backgroundColor: background, color: foreground, borderRadius: "11px", padding: "8px 10px", cursor: "pointer", fontSize: "0.76rem", fontWeight: 700 }}
			>
				Retry issuer accounts
			</Box>
		);
	}

	if (issuers.length === 0) {
		return (
			<VBox gap="1px" sx={{ minWidth: 0 }}>
				<Typo sx={{ color: foreground, fontSize: "0.76rem", fontWeight: 700 }}>No issuer assigned</Typo>
				{!compact && <Typo sx={{ color: muted, fontSize: "0.66rem" }}>Contact support to enable funding.</Typo>}
			</VBox>
		);
	}

	return (
		<HBox
			gap="9px"
			role="group"
			aria-label={`Active issuer: ${selectedIssuerLabel}`}
			data-active-issuer={selectedIssuerId ?? "none"}
			data-issuer-accent={identity.accent}
			sx={{ alignItems: "center", minWidth: 0 }}
		>
			{issuers.length === 1 ? (
				<Box
					data-single-issuer="true"
					sx={{
						width: compact ? "112px" : "clamp(180px, 21vw, 260px)",
						[MOBILE]: compact ? { width: "min(210px, calc(100vw - 164px))" } : {},
						maxWidth: "100%",
						height: compact ? "36px" : "38px",
						minHeight: compact ? "36px" : "38px",
						border: `1px solid ${border}`,
						borderRadius: "11px",
						backgroundColor: dark ? background : "#FFFFFF",
						boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,.04)",
						overflow: "hidden",
					}}
				>
					<IssuerSelectItem
						value={selectedIssuer}
						prompt
						dark={dark}
						compact={compact}
						interactive={false}
					/>
				</Box>
			) : (
			<Select<ICompanyIssuer>
				values={issuers}
				value={selectedIssuer ?? undefined}
				item={<IssuerSelectItem value={null} dark={dark} compact={compact} />}
				onChange={issuer => {
					S_UNIVERSAL_DIALOG.invoke(null);
					selectIssuer(issuer.issuer);
				}}
				disabled={loading}
				readOnly={issuers.length <= 1}
				size="small"
				simple
				ariaLabel={`Active issuer account, ${selectedIssuerLabel}`}
				sheetLabel="Switch issuer"
				sheetAlign="right"
				sheetOffset={8}
				sheetWidth="268px"
				minSheetWidth="268px"
				bg={dark ? "#132C28" : "#FFFFFF"}
				sx={{
					width: compact ? "112px" : "clamp(180px, 21vw, 260px)",
					[MOBILE]: compact ? { width: "min(210px, calc(100vw - 164px))" } : {},
					maxWidth: "100%",
					height: compact ? "36px" : "38px",
					minHeight: compact ? "36px" : "38px",
					border: `1px solid ${border}`,
					borderRadius: "11px",
					backgroundColor: dark ? background : "#FFFFFF",
					boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,.04)",
					cursor: issuers.length > 1 ? "pointer" : "default",
					opacity: loading ? 0.65 : 1,
					transition: "border-color .18s ease, box-shadow .18s ease, background-color .18s ease",
					"&:hover": {
						border: `1px solid ${identity.accent}`,
						backgroundColor: dark ? "rgba(255,255,255,.1)" : identity.surface,
						boxShadow: dark ? "none" : "0 4px 12px rgba(15,23,42,.07)",
					},
					"&:focus-visible": {
						outline: "none",
						border: `1px solid ${identity.accent}`,
						boxShadow: dark ? "0 0 0 2px rgba(255,255,255,.12)" : `0 0 0 2px ${identity.surface}`,
					},
					"&[aria-expanded='true']": {
						border: `1px solid ${identity.accent}`,
						backgroundColor: dark ? "rgba(255,255,255,.1)" : identity.surface,
						boxShadow: dark ? "0 0 0 2px rgba(255,255,255,.12)" : `0 0 0 2px ${identity.surface}`,
					},
					"&[aria-expanded='true'] [data-issuer-chevron='true']": {
						transform: "rotate(180deg)",
					},
				}}
			/>
			)}
			{!compact && selectedIssuer && (
				<VBox gap="1px" sx={{ alignItems: "flex-end", minWidth: "88px", [MOBILE]: { display: "none" } }}>
					<Typo sx={{ color: muted, fontSize: "0.61rem", lineHeight: 1, fontWeight: 700, textTransform: "uppercase" }}>{balanceLoading ? "Updating" : balanceUnavailable ? "Refresh failed" : "Available"}</Typo>
					{balanceLoading ? (
						<Box role="status" aria-label="Updating available balance" sx={{ minHeight: "18px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
							<Spinner size={16} color={foreground} />
						</Box>
					) : balanceUnavailable ? (
						<Box
							element="button"
							type="button"
							title={error ?? undefined}
							onClick={() => void refreshIssuers()}
							sx={{ padding: 0, border: 0, color: foreground, background: "transparent", font: "inherit", fontSize: "0.75rem", fontWeight: 800, cursor: "pointer", textDecoration: "underline" }}
						>
							Retry balance
						</Box>
					) : (
						<Typo sx={{ color: foreground, fontSize: "0.82rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
							{formatCurrencyWithNumberToSymbolWithNumber(selectedIssuer.balance, selectedIssuer.currency)}
						</Typo>
					)}
				</VBox>
			)}
		</HBox>
	);
};
